import { useTranslation } from 'react-i18next';
import {
  useDeleteExerciseProperty,
  useGetExerciseProperties,
} from '../../../services/redux/api/exerciseConfigAPI';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { FormikProps, useFormik } from 'formik';

export function useExercisePropertyComponent(classes) {
  const { t } = useTranslation();
  const { data: exerciseProperties } = useGetExerciseProperties();
  const [itemForDelete, setItemForDelete] = useState<IExerciseProperty>(null);
  const [itemForEditing, setItemForEditing] = useState<IExerciseProperty>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteExerciseProperty] = useDeleteExerciseProperty();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);

  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      parent: { id: 'all', name: t('common.all') },
      type: { id: 'all', name: t('common.all') },
      active: { id: 'all', name: t('common.all') },
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      exerciseProperties?.filter((property: IExerciseProperty) => {
        const parentFilterId = filterFormik.values.parent?.id ?? 'all';
        const typeFilterId = filterFormik.values.type?.id ?? 'all';
        const activeFilterId = filterFormik.values.active?.id ?? 'all';
        const filterValue = filter.toLowerCase();

        if (
          !String(property.name ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(property.nameKey ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(property.type ?? '')
            .toLowerCase()
            .includes(filterValue)
        ) {
          return false;
        }

        if (parentFilterId !== 'all' && property.parent?.id !== parentFilterId)
          return false;

        if (typeFilterId !== 'all' && property.type !== typeFilterId)
          return false;

        if (activeFilterId !== 'all') {
          if (activeFilterId === true && property.active) {
            return true;
          } else if (activeFilterId === false && !property.active) {
            return true;
          }
          return false;
        }

        return true;
      }),
    [exerciseProperties, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.exerciseProperties?.canEdit ?? false,
      canDelete: access?.exerciseProperties?.canDelete ?? false,
    },
    (property: IExerciseProperty) => {
      setItemForEditing(property);
      setShowEditModal(true);
    },
    (property: IExerciseProperty) => {
      setItemForDelete(property);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.EXERCISE_PROPERTY_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const { order, columnsList, sortedArray, onSortColumns, onSort } =
    useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteExerciseProperty({ id: itemForDelete.id });
  }

  const filterParentOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    ...Array.from(new Set(exerciseProperties?.map((p) => p.id) || [])).map(
      (id) => ({
        id: id,
        name: exerciseProperties.find((p) => p.id === id)?.name,
      }),
    ),
  ];

  const filterTypeOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    ...Array.from(new Set(exerciseProperties?.map((p) => p.type) || [])).map(
      (type) => {
        return {
          id: type,
          name: type,
        };
      },
    ),
  ];

  const filterActiveTranslationOptions = [
    {
      id: 'all',
      name: t('common.all'),
    },
    {
      id: true,
      name: t('common.active'),
    },
    {
      id: false,
      name: t('common.notActive'),
    },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterParentOptions,
    filterTypeOptions,
    filterActiveTranslationOptions,
  };
}
